import { isEmergencyDepartment } from "./facilityTypes";

export const DIAGNOSTIC_CAPABILITY_OPTIONS = [
  { key: "xray", label: "X-Ray", category: "Imaging" },
  { key: "ct_scan", label: "CT Scan", category: "Imaging" },
  { key: "mri", label: "MRI", category: "Imaging" },
  { key: "ultrasound", label: "Ultrasound", category: "Imaging" },
  { key: "cbc", label: "CBC (Complete Blood Count)", category: "Lab" },
  { key: "bmp", label: "Basic Metabolic Panel", category: "Lab" },
  { key: "urinalysis", label: "Urinalysis", category: "Lab" },
  { key: "rapid_strep", label: "Rapid Strep", category: "Lab" },
  { key: "rapid_flu", label: "Rapid Flu", category: "Lab" },
  { key: "rapid_covid", label: "Rapid COVID", category: "Lab" },
  { key: "ecg", label: "ECG / EKG", category: "Procedures" },
  { key: "iv_fluids", label: "IV Fluids", category: "Procedures" },
  { key: "laceration_repair", label: "Laceration Repair", category: "Procedures" },
  { key: "fracture_management", label: "Fracture Management", category: "Procedures" },
];

export const DIAGNOSTIC_CAPABILITY_KEYS = DIAGNOSTIC_CAPABILITY_OPTIONS.map(
  (o) => o.key
);

export const DIAGNOSTIC_IMAGING_CAPABILITY_KEYS = [
  "xray",
  "ct_scan",
  "mri",
  "ultrasound",
];

export const DIAGNOSTIC_LAB_CAPABILITY_KEYS = [
  "cbc",
  "bmp",
  "urinalysis",
  "rapid_strep",
  "rapid_flu",
  "rapid_covid",
];

export const DIAGNOSTIC_PROCEDURE_CAPABILITY_KEYS = [
  "ecg",
  "iv_fluids",
  "laceration_repair",
  "fracture_management",
];

// Capabilities whose NEED implies an emergency-department-level workup. Advanced
// cross-sectional imaging (CT/MRI) is effectively never available same-day at an
// urgent care or clinic, so if the patient's diagnosticNeeds include any of these,
// a care-level downgrade OUT of the ER is NOT confirmed by the test profile.
// INVARIANT: must stay a strict subset of DIAGNOSTIC_CAPABILITY_KEYS. Keep this
// list conservative — adding a key here makes downgrades HARDER (safer), removing
// one makes them easier.
// See: SC2_CAREMAP2_CAPABILITY_ATTESTATION_AND_MATCHING.md §2
export const ER_CLASS_CAPABILITY_KEYS = Object.freeze(["ct_scan", "mri"]);

export const DIAGNOSTIC_CAPABILITY_CATEGORY_ORDER = [
  "Imaging",
  "Lab",
  "Procedures",
];

export const DIAGNOSTIC_CAPABILITY_KEYS_BY_CATEGORY = {
  Imaging: DIAGNOSTIC_IMAGING_CAPABILITY_KEYS,
  Lab: DIAGNOSTIC_LAB_CAPABILITY_KEYS,
  Procedures: DIAGNOSTIC_PROCEDURE_CAPABILITY_KEYS,
};

export const CAPABILITY_VERIFICATION_STATUS = Object.freeze({
  VERIFIED: "verified",
  DUE_SOON: "due_soon",
  OVERDUE: "overdue",
  UNVERIFIED: "unverified",
});

export const CAPABILITY_VERIFICATION_ELIGIBLE_STATUSES = Object.freeze([
  CAPABILITY_VERIFICATION_STATUS.VERIFIED,
  CAPABILITY_VERIFICATION_STATUS.DUE_SOON,
]);

export const EMERGENCY_DEPARTMENT_TYPE = "Emergency Department";

/** @param {object | null | undefined} locationDoc */
export function isEmergencyDepartmentLocation(locationDoc) {
  // Delegates to facilityTypes.js — do not duplicate ED detection logic.
  return isEmergencyDepartment(locationDoc);
}

/**
 * Capabilities used for CareMap / CareMap 2 matching and facility cards.
 * Emergency Departments assume the full capability set until they store a
 * non-empty downgrade list in diagnosticCapabilities (future self-service).
 *
 * @param {object | null | undefined} locationDoc
 * @returns {string[]}
 */
export function getEffectiveDiagnosticCapabilities(locationDoc) {
  const stored = locationDoc?.diagnosticCapabilities;
  if (isEmergencyDepartmentLocation(locationDoc)) {
    if (Array.isArray(stored) && stored.length > 0) {
      return stored.filter((key) => DIAGNOSTIC_CAPABILITY_KEYS.includes(key));
    }
    return [...DIAGNOSTIC_CAPABILITY_KEYS];
  }
  return Array.isArray(stored) ? stored : [];
}
